import { SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
  Pagination
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from "../../Images/ArchiveRestore.svg";
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedDieSubCategoryList, unArchiveDieSubCategory } from './../../slices/die-subcategory';
import dieSubCategoryService from '../../services/die-subcategorylist.service';

export default function ArchiveDieSubCategories() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [pagesize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data = [], totalRecord, ordering, status } = useSelector(state => state.dieSubCategory || {});

  useEffect(() => {
    dispatch(
      fetchArchivedDieSubCategoryList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to the first page when searching
  };
  const resetFilter = () => {
    setSearch('');
    setPage(1);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "name" && sortOrder === "ascend") {
      sortingEnumKey = "name";
    }
    if (sortField === "name" && sortOrder === "descend") {
      sortingEnumKey = "-name";
    }
    if (sortField === "updated_at" && sortOrder === "ascend") {
      sortingEnumKey = "updated_at";
    }
    if (sortField === "updated_at" && sortOrder === "descend") {
      sortingEnumKey = "-updated_at";
    }
    if (sortField === "updated_by" && sortOrder === "ascend") {
      sortingEnumKey = "updated_by";
    }
    if (sortField === "updated_by" && sortOrder === "descend") {
      sortingEnumKey = "-updated_by";
    }
    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter && sorter.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));


  const handleRestore = async id => {
    if (!id) {
      message.error('Missing ID for restore!');
      return;
    }

    try {
      await dieSubCategoryService.restoreDieSubCategory(id);
      message.success('Section sub category restored successfully');
      setSelectedRowKeys([]);
      dispatch(fetchArchivedDieSubCategoryList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore section sub category';
      message.error(errorMessage);
    }
  };
  const handleBulkRestore = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to restore');
      return;
    }

    try {
      await dieSubCategoryService.restoreDieSubCategory(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Section sub category(s) restored successfully`,
      );
      setSelectedRowKeys([]);
      dispatch(fetchArchivedDieSubCategoryList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore section sub categories';
      message.error(errorMessage);
    }
  };
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,

    },
    {
      title: 'Sub Category Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      width: 100,
      render: (record) => {
        return (
          <span>{record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 100,
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 100,
        render: (_, record) => (
        <Space>
              <Tooltip placement="top" title="Restore">
            <Popconfirm
                title="Are you sure you want to restore this section sub category?"
                onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                onCancel={() => message.info('Action cancelled')} // Optional cancel action
                okText="Yes"
                cancelText="No"
              >
                <ArchiveRestore className="ArchiverestorImg" style={{ cursor: 'pointer' }} />
              </Popconfirm>
            </Tooltip>
        </Space>
      ),
    },
  ];


  return (
    <>
      <div className='sticyaction'>
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Archived Section Sub Categories</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-subcategory" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/die-subcategory">Section Sub Categories</Link> },
                  { title: 'Archived Section Sub Categories' }
                ]}
              />
            </div>
          </Col>
          <Col>
          <Space size="large">
              <Input
                placeholder="Search"
                className='searchbox'
                prefix={<SearchOutlined />}
                value={search}
                style={{ width: 200 }}
                onChange={handleSearch}
              />
          
              <Tooltip
                title={
                  selectedRowKeys.length === 0
                    ? 'Select at least one record to restore'
                    : `Restore ${selectedRowKeys.length} selected record(s)`
                }
              >
                <Popconfirm
                  title={`Are you sure you want to restore ${selectedRowKeys.length} selected section sub category(s)?`}
                  onConfirm={handleBulkRestore}
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                  >
                    Restore Selected
                  </Button>
                </Popconfirm>
              </Tooltip>
                <Tooltip placement="top" title={'Reset Filter'}>
                  <Button type="primary" disabled={!search} className='iconlink' onClick={resetFilter}>
                    <FilterIcon />
                  </Button>
                </Tooltip>
           
            </Space>
          </Col>
        </Row>
      </div>
      <Row gutter={[16, 16]}>
      </Row>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Table
              rowSelection={rowSelection}
            dataSource={dataSource}
            columns={columns}
            loading={status === 'loading'}
            pagination={false}
            onChange={handleTableChange}
            scroll={{
              x: 200,
              y: 350,
            }}
          />
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["20", "50", "100"]}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}
